import Cookies from 'js-cookie';
import { encode } from 'js-base64';
import { md5 as jsmd5 } from 'js-md5';
import { v4 as uuidv4 } from 'uuid';
import { BASE_URL } from '@/constants';
import env from './env';
import { EMOJI_SET } from '@/constants/emoji';
import { formatRelative, startOfDay, endOfDay, subDays, subMonths } from 'date-fns';
import { DateType, TimeRange } from '@/interfaces/bi';
import { isNumber } from 'lodash-es';

/** 跳转小红书聚光平台 */
export const openRedbookAdUrl = () => window.open(env.REDBOOK_AD_URL, '_blank');

const TOKEN = '_authorization_v2';
const ENT_ID = '_ent_id_v2';
const AGENT_ID = '_agent_id_v2';
const AGENT_NAME = '_agent_name_v2';
const AGENT_AVATAR = '_agent_avatar_v2';
const ENT_NAME = '_ent_name_v2';

const setAuthCookie = (key: string, value: string) =>
  Cookies.set(key, value, {
    expires: 3650,
    path: '/',
    domain: window.location.hostname,
    sameSite: 'none',
    secure: true,
  });

export const setAuthCookies = (data: {
  token: string;
  entId: number;
  id: number;
  entName: string;
}) => {
  setAuthCookie(TOKEN, data.token);
  setAuthCookie(ENT_ID, data.entId.toString());
  setAuthCookie(AGENT_ID, data.id.toString());
  setAuthCookie(ENT_NAME, data.entName);
  // setAuthCookie(AGENT_NAME, data.name);
  // setAuthCookie(AGENT_AVATAR, data.avatar);
};
export const clearAuthCookies = () => {
  Cookies.remove(TOKEN);
  Cookies.remove(ENT_ID);
  Cookies.remove(AGENT_ID);
  Cookies.remove(AGENT_NAME);
  Cookies.remove(AGENT_AVATAR);
  Cookies.remove(ENT_NAME);
};

export const getAuthCookies = () => {
  return {
    token: Cookies.get(TOKEN)!,
    entId: Cookies.get(ENT_ID)!,
    agentId: Cookies.get(AGENT_ID)!,
  };
};

export const getToken = () => Cookies.get(TOKEN);
export const getAgentName = () => Cookies.get(AGENT_NAME)!;
export const getAgentAvatar = () => Cookies.get(AGENT_AVATAR)!;
export const getAgentId = () => Number(Cookies.get(AGENT_ID));
export const getEntId = () => Number(Cookies.get(ENT_ID));
export const getEntName = () => Cookies.get(ENT_NAME)!;

export const md5 = (value: string) => encode(jsmd5(value));

export const getQueryString = (name: string, url: string) => {
  const parserUrl = url || window.location.search;
  const param = new URLSearchParams(parserUrl);
  return param.get(name);
};

export const uuid = () => uuidv4().replace(/-/g, '');

/**
 * 生成8位随机字符串
 * 确保唯一性和可读性
 * @returns 8位随机字符串
 */
export const generateCheckCode = (): string => {
  // 指定字符集，排除容易混淆的字符
  const chars = '23456789abcdefghjkmnpqrstuvwxyz';
  let result = '';

  // 使用 crypto.getRandomValues 如果可用，否则使用 Math.random
  const getRandomChar = (): string => {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const array = new Uint8Array(1);
      crypto.getRandomValues(array);
      return chars[array[0] % chars.length];
    } else {
      return chars[Math.floor(Math.random() * chars.length)];
    }
  };

  // 生成8位随机字符
  for (let i = 0; i < 8; i++) {
    result += getRandomChar();
  }
  return result;
};

export const convertTextMessage = (content: string) => {
  const result = content.replace(/\[[^[\]]+\]/g, (match) => {
    if (!EMOJI_SET.has(match)) {
      return match;
    }
    const filename = match.slice(1, -1);
    return `<img class="inline align-text-bottom" width="16" height="16" src="${BASE_URL}/emoji/${filename}.png" />`;
  });
  return result;
};

export const highlightText = (content: string, keyword: string | undefined) => {
  if (!keyword) return content;

  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedKeyword})`, 'gi');
  return content.replace(regex, '<span class="bg-yellow-300">$1</span>');
};

export const isValueEmpty = (value: any): boolean => {
  // 处理 null 和 undefined
  if (value === null || value === undefined) {
    return true;
  }

  // 处理字符串
  if (typeof value === 'string') {
    return value.trim().length === 0;
  }

  // 处理数组
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  // 处理对象
  if (typeof value === 'object') {
    return Object.values(value).every(isValueEmpty);
  }

  // 处理数字 - 0 不认为是空值
  if (typeof value === 'number') {
    return false;
  }

  // 处理布尔值 - 布尔值都不认为是空值
  if (typeof value === 'boolean') {
    return false;
  }

  return false;
};

export const getUpdateTime = (time: number): string => formatRelative(time, new Date());

export const isRegisterEnabled = () => env.DISABLE_REGISTER !== 'true';

export const canEditTemplate = () => {
  const enableEntIds = (env.TEMPLATE_EDITABLE_ENT_IDS?.split(',') || []).map(Number);
  return enableEntIds.includes(getEntId());
};

export const getStartAndEndTime = (date: DateType, customRange?: [number, number]): TimeRange => {
  const now = new Date();
  const today = {
    start: startOfDay(now),
    end: endOfDay(now),
  };

  const timeRanges: Record<Exclude<DateType, 'custom'>, TimeRange> = {
    today: {
      startTime: today.start.getTime(),
      endTime: today.end.getTime(),
    },
    yesterday: {
      startTime: startOfDay(subDays(now, 1)).getTime(),
      endTime: endOfDay(subDays(now, 1)).getTime(),
    },
    week: {
      startTime: startOfDay(subDays(now, 6)).getTime(),
      endTime: today.end.getTime(),
    },
    lastWeek: {
      startTime: startOfDay(subDays(now, 7)).getTime(),
      endTime: endOfDay(subDays(now, 1)).getTime(),
    },
    month: {
      startTime: startOfDay(subMonths(now, 1)).getTime(),
      endTime: today.end.getTime(),
    },
    recent7Days: {
      startTime: startOfDay(subDays(now, 7)).getTime(),
      endTime: endOfDay(subDays(now, 1)).getTime(),
    },
    recent30Days: {
      startTime: startOfDay(subDays(now, 30)).getTime(),
      endTime: endOfDay(subDays(now, 1)).getTime(),
    },
  };

  if (date === 'custom') {
    if (!customRange || !customRange[0] || !customRange[1]) {
      return timeRanges.yesterday; // 默认返回昨天的数据
    }
    return {
      startTime: customRange[0],
      endTime: customRange[1],
    };
  }

  return timeRanges[date] || timeRanges.yesterday;
};

export const getTimeZone = () => {
  const offsetMinutes = new Date().getTimezoneOffset();
  const offsetHours = Math.abs(offsetMinutes / 60);
  const offsetSign = offsetMinutes > 0 ? '-' : '+';
  return `${offsetSign}${offsetHours}`;
};

export const getRate = (numerator?: number, denominator?: number, precision?: number) => {
  if (!numerator || !denominator || denominator === 0) {
    return 0;
  }
  const rate = ((numerator * 100) / denominator).toFixed(precision ?? 2);
  return Number(rate) % 1 === 0 ? Math.floor(Number(rate)) : Number(rate);
};

export const calculatePercentageChange = (
  number?: number,
  prevNumber?: number,
  precision?: number,
) => {
  if (prevNumber === 0) return number === 0 ? 0 : 100;
  if (number === 0 && prevNumber !== 0) return 100;
  if (!number || !prevNumber) return 0;
  const change = (((number - prevNumber) * 100) / prevNumber).toFixed(precision ?? 2);
  return Number(change) % 1 === 0 ? Math.floor(Number(change)) : Number(change);
};

export const getRateByRate = (rate: number) => {
  if (rate === -1) return '--';
  if (rate === 0) return '0%';
  return `${(rate * 100).toFixed(2)}%`;
};

export const dealPrice = (
  num: number | string,
  noCurrency = false,
  minimumFractionDigits: number = 0,
  maximumFractionDigits: number = 0,
) => {
  let data = isNumber(num) ? num : +num;
  data = data || 0;

  // 智能小数位：如果有用户指定的小数位，使用用户指定的值；否则根据数值是否有小数部分来决定
  const hasDecimalPart = data % 1 !== 0;
  const actualMinDigits = hasDecimalPart
    ? Math.max(minimumFractionDigits, 2)
    : minimumFractionDigits;
  const actualMaxDigits = hasDecimalPart
    ? Math.max(maximumFractionDigits, 2)
    : maximumFractionDigits;

  return data.toLocaleString(
    'zh-Hans-CN',
    noCurrency
      ? {
          currency: 'CNY',
          minimumFractionDigits: actualMinDigits,
          maximumFractionDigits: actualMaxDigits,
        }
      : {
          style: 'currency',
          currency: 'CNY',
          minimumFractionDigits: actualMinDigits,
          maximumFractionDigits: actualMaxDigits,
        },
  );
};

export const formatNumberWithThousands = (
  num: number | string,
  minimumFractionDigits: number = 0,
  maximumFractionDigits: number = 0,
) => {
  return dealPrice(num, true, minimumFractionDigits, maximumFractionDigits);
};

export const formatPriceDivide100 = (num: number) => {
  return `${formatNumberWithThousands(num / 100)}`;
};

export const getValueOrDie = <T>(value: T | null | undefined): T => {
  if (value === null || value === undefined) {
    throw new Error('value is null or undefined');
  }
  return value;
};

export function sleep(time: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, time));
}

// 计算百分比指标的环比增长率
export const calculateRateGrowth = (currentRate?: number, previousRate?: number): number => {
  currentRate = currentRate ?? 0;
  previousRate = previousRate ?? 0;
  if (previousRate === 0) {
    return currentRate === 0 ? 0 : 100;
  }
  return ((currentRate - previousRate) / previousRate) * 100;
};
